import { Navigate, Outlet, useParams } from 'react-router-dom'

import type { User, UserTab } from '@/api/types'
import { PageHeader } from '@/app/layouts/PageHeader'
import { EmptyState, ErrorState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Skeleton'
import { PostList } from '@/features/posts'
import { ProfileHeader, ProfileTabs, useProfile } from '@/features/profile'
import { useUserTimeline } from '@/features/timeline'
import { useT } from '@/i18n'
import { useFormat } from '@/lib/format'
import { ApiError } from '@/api/client'

export function ProfilePage() {
  const { compactCount } = useFormat()
  const t = useT()
  const { username = '' } = useParams()
  const profile = useProfile(username)

  if (profile.isPending) {
    return (
      <>
        <PageHeader title={t('profile.title')} back />
        <Skeleton className="h-48 rounded-none" />
        <div className="space-y-2 px-4 py-4">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-24" />
        </div>
      </>
    )
  }

  if (profile.isError) {
    const error = profile.error instanceof ApiError ? profile.error : null
    const notFound = error?.status === 404
    // 被封禁的如实说明（D66）；自己停用的对外就是不存在
    const message =
      error?.code === 'user_suspended'
        ? t('profile.suspended')
        : notFound
          ? t('profile.notFound')
          : profile.error.message
    return (
      <>
        <PageHeader title={t('profile.title')} back />
        <ErrorState message={message} onRetry={notFound ? undefined : () => profile.refetch()} />
      </>
    )
  }

  const user = profile.data

  return (
    <>
      <PageHeader
        title={user.display_name}
        subtitle={t('profile.postCount', { count: compactCount(user.counts.posts) })}
        back
      />
      <ProfileHeader user={user} />
      <ProfileTabs username={user.username} />
      {canSeeContent(user) ? (
        <Outlet context={{ username: user.username }} />
      ) : (
        // 受保护的账号（D75）：资料照常，帖子与喜欢只给本人和获准的关注者
        <EmptyState
          title={t('protected.postsTitle')}
          description={t('protected.postsBody', { username: user.username })}
        />
      )}
    </>
  )
}

/** 四个 tab 共用一个列表组件，tab 由路由决定。 */
export function ProfileTimeline({ tab }: { tab: UserTab }) {
  const t = useT()
  const { username = '' } = useParams()
  const timeline = useUserTimeline(username, tab)

  const empty: Record<UserTab, string> = {
    posts: t('profile.emptyPosts'),
    replies: t('profile.emptyReplies'),
    media: t('profile.emptyMedia'),
    likes: t('profile.emptyLikes'),
  }

  // 置顶帖只在「帖子」tab 的第一页给（D69）
  const pinned = tab === 'posts' ? (timeline.data?.pages[0]?.pinned ?? null) : null

  return <PostList query={timeline} emptyTitle={empty[tab]} pinned={pinned} />
}

export function ProfileRedirect() {
  const { username = '' } = useParams()
  return <Navigate to={`/${username}`} replace />
}

/** 受保护账号的帖子只给本人与已批准的关注者（D75） */
function canSeeContent(user: User): boolean {
  return !user.is_private || Boolean(user.viewer?.is_self || user.viewer?.following)
}
